import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import firebase from 'firebase/compat/app';

import { isInternalAccount } from '../../core/helpers/internal-accounts';
import { effectiveApr, isEnabled, isManagedByCatalog } from '../../pages/currencies/shared';
import {
  BOOST_DEFAULTS, boostPctFor, effectiveTier, isClosed, isUserSuspended, tierThresholds,
} from '../../pages/users/shared';

// ─────────────────────────────────────────────────────────────────────────────
// ESTE ARCHIVO ES .ts A PROPÓSITO, aunque exporte un proveedor de React.
//
// fase63 lo había nombrado `.tsx`. La entrega se copia SOBRE el repo y copiar
// nunca borra: en el repo quedaron `libro.ts` (viejo) y `libro.tsx` (nuevo)
// conviviendo. Webpack resuelve `.ts` ANTES que `.tsx`, así que el import
// `console/data/libro` se fue al archivo viejo y el build de DigitalOcean cortó
// con «'LibroProvider' is not exported». Manteniendo el nombre `.ts`, el zip
// PISA el archivo anterior y la colisión no puede volver a existir. El único
// nodo JSX del archivo se escribe con `React.createElement`.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// EL LIBRO — lectura ÚNICA por sesión de todo lo que la consola necesita para
// hablar de dinero: catálogo, cuentas, tenencias, precios y liquidaciones.
//
// fase63 — ESTO ERA UN HOOK POR PANTALLA Y AHORA ES UN CONTEXTO. Seis de las
// siete pantallas lo piden; con un hook suelto, cada navegación entre pestañas
// releía la base entera. Ahora se lee una vez, se comparte, y se refresca a
// pedido o cuando se pasa el tiempo de vigencia.
//
// Tres decisiones que hacen la diferencia:
//
//  1. CACHÉ DE MÓDULO + DEDUPE DE VUELO. La instantánea vive fuera de React. Si
//     dos pantallas la piden a la vez, comparten LA MISMA promesa; si una vuelve
//     antes de que venza, no se lee nada.
//
//  2. PRECIO = ÚLTIMO CIERRE, NO LA SERIE. Antes se traían AÑOS de cierres por
//     ticker, con un `await` por año dentro del bucle, más un rango multianual
//     de Frankfurter. Ninguna pantalla usaba esas series: venían arrastradas de
//     la página vieja de Tenencias, cuya curva de rendimiento de swaps no se
//     portó. Ahora es UNA lectura por ticker (el año en curso, con el anterior
//     sólo de respaldo si el año recién arrancó) y todas en paralelo.
//
//  3. DOS ETAPAS. Lo que necesita CASI TODA la consola entra en la primera. Los
//     swaps —que exigen recorrer las transacciones— van a la segunda y sólo se
//     leen cuando alguien abre Analíticas · Tenencias, que es la única pantalla
//     que los mira.
//
// Ninguna consulta cambió de forma ni de filtro: los mismos `where`, los mismos
// límites y la misma exclusión de cuentas internas que traía la página vieja.
// ─────────────────────────────────────────────────────────────────────────────

export type CurRow = {
  id: string; name: string; fullName: string; category: string;
  enabled: boolean; managed: boolean; pegUsd: boolean; apr: number;
  priceSource?: string; railToken?: string; raw: any;
};

export type UserRow = {
  uid: string; name: string; email: string; isAdmin: boolean; interno: boolean;
  estado: 'active' | 'admin_suspended' | 'user_suspended';
  tier?: string; points: number | null; boost: number; ultra: boolean;
  ultraApr: Record<string, number>;
  lastLoginAt: Date | null; createdAt: Date | null; raw: any;
};

export type Hold = { uid: string; currency: string; amount: number; reserved: number };

export type LiqDoc = {
  date: string; totalUsd: number; users?: number; interests?: number;
  transactions?: number; byAsset?: Record<string, number>;
  byUser?: Record<string, { amountUsd: number; byCurrency?: Record<string, number> }>;
  backfilled?: boolean;
};

export type SwapTx = {
  uid: string; fromCur: string; fromQty: number; toCur: string; toQty: number; d0: string;
};

export type Libro = {
  loading: boolean;
  loadedAt: number;
  currencies: CurRow[];
  curById: Record<string, CurRow>;
  tickerOf: (id: string) => string;
  idOfTicker: (t: string) => string | undefined;
  users: UserRow[];
  userById: Record<string, UserRow>;
  holds: Hold[];
  prices: Record<string, number>;
  priceMisses: string[];
  liqs: LiqDoc[];
  gamification: any;
  /** Segunda etapa: sólo con datos si alguien la pidió. */
  swaps: SwapTx[];
  swapsListos: boolean;
  /** Dispara la segunda etapa. Idempotente. */
  pedirProfundo: () => void;
  reload: () => void;
};

type Snap = Omit<Libro, 'pedirProfundo' | 'reload' | 'swaps' | 'swapsListos'>;

const VACIO: Snap = {
  loading: true, loadedAt: 0, currencies: [], curById: {},
  tickerOf: (id) => id, idOfTicker: () => undefined,
  users: [], userById: {}, holds: [], prices: {}, priceMisses: [],
  liqs: [], gamification: null,
};

// ── Caché de módulo ──────────────────────────────────────────────────────────
const VIGENCIA_MS = 90_000;
let cache: Snap | null = null;
let enVuelo: Promise<Snap> | null = null;
let swapsCache: SwapTx[] | null = null;
let swapsEnVuelo: Promise<SwapTx[]> | null = null;
const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach((f) => f());

export function invalidarLibro() {
  cache = null;
  enVuelo = null;
  swapsCache = null;
  swapsEnVuelo = null;
}

function toD(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

async function leerLibro(): Promise<Snap> {
  const db = firebase.firestore();
  const [curSnap, invSnap, userSnap, liqSnap, gamSnap] = await Promise.all([
    db.collection('currencies').get(),
    db.collection('investments').get(),
    db.collection('users').limit(600).get(),
    db.collection('interest-liquidations').get(),
    db.collection('settings').doc('gamification').get(),
  ]);

  const gamification = gamSnap.exists ? gamSnap.data() : null;
  const thr = tierThresholds(gamification);
  const boostPcts = {
    premium: Number.isFinite(Number(gamification?.premiumBoostPct))
      ? Number(gamification?.premiumBoostPct) : BOOST_DEFAULTS.premium,
    elite: Number.isFinite(Number(gamification?.eliteBoostPct))
      ? Number(gamification?.eliteBoostPct) : BOOST_DEFAULTS.elite,
  };

  const currencies: CurRow[] = curSnap.docs.map((d) => {
    const x = d.data() as Record<string, any>;
    return {
      id: d.id,
      name: String(x?.name ?? d.id).toUpperCase(),
      fullName: String(x?.fullName ?? ''),
      category: String(x?.category ?? ''),
      enabled: isEnabled(x),
      managed: isManagedByCatalog(x),
      pegUsd:
        x?.pegUsd === true ||
        String(x?.priceSource ?? '') === 'peg' ||
        String(x?.name ?? '').toUpperCase() === 'USD',
      apr: effectiveApr(x) ?? 0,
      priceSource: x?.priceSource,
      railToken: x?.railToken,
      raw: { ...x, id: d.id },
    };
  });
  const curById: Record<string, CurRow> = {};
  currencies.forEach((c) => (curById[c.id] = c));
  const idByTicker: Record<string, string> = {};
  currencies.forEach((c) => (idByTicker[c.name] = c.id));

  const users: UserRow[] = userSnap.docs.map((d) => {
    const x = d.data() as Record<string, any>;
    const email = String(x?.email ?? '');
    const tier = effectiveTier({ ...x, id: d.id }, thr);
    return {
      uid: d.id,
      name: String(x?.fullName ?? '').trim() || email || d.id,
      email,
      isAdmin: x?.isAdmin === true,
      interno: isInternalAccount(d.id, email),
      estado: isClosed(x) ? 'admin_suspended' : isUserSuspended(x) ? 'user_suspended' : 'active',
      tier,
      points: x?.points === undefined || x?.points === null ? null : Number(x.points),
      boost: boostPctFor(tier, boostPcts),
      ultra: tier === 'ultra',
      ultraApr: (x?.ultraApr ?? {}) as Record<string, number>,
      lastLoginAt: toD(x?.lastLoginAt),
      createdAt: toD(x?.createdAt),
      raw: { ...x, id: d.id },
    };
  });
  const userById: Record<string, UserRow> = {};
  users.forEach((u) => (userById[u.uid] = u));

  const interno = (uid: string) =>
    !!userById[uid]?.interno || isInternalAccount(uid, userById[uid]?.email);

  const holds: Hold[] = invSnap.docs
    .map((d) => {
      const x = d.data() as Record<string, any>;
      return {
        uid: String(x?.uid ?? ''),
        currency: String(x?.currency ?? ''),
        amount: Number(x?.amount ?? 0),
        reserved: Number(x?.reserved ?? 0),
      };
    })
    .filter((r) => r.uid && r.currency && (r.amount > 0 || r.reserved > 0))
    .filter((r) => !interno(r.uid));

  // Los resúmenes nuevos ya nacen sin cuentas internas (composer fase46); este
  // strip cubre lo escrito ANTES de ese deploy.
  const stripInternal = (l: LiqDoc): LiqDoc => {
    const byUser = l.byUser ?? {};
    const internos = Object.keys(byUser).filter((uid) => interno(uid));
    if (internos.length === 0) return l;
    let totalUsd = Number(l.totalUsd ?? 0);
    const byAsset: Record<string, number> = { ...(l.byAsset ?? {}) };
    const byUserOut = { ...byUser };
    internos.forEach((uid) => {
      const row = byUserOut[uid];
      totalUsd -= Number(row?.amountUsd ?? 0);
      Object.entries(row?.byCurrency ?? {}).forEach(([c, v]) => {
        byAsset[c] = (byAsset[c] ?? 0) - Number(v ?? 0);
        if (!(byAsset[c] > 1e-8)) delete byAsset[c];
      });
      delete byUserOut[uid];
    });
    return {
      ...l,
      totalUsd: Math.max(0, Math.round(totalUsd * 1e8) / 1e8),
      users: Object.keys(byUserOut).length,
      byAsset, byUser: byUserOut,
    };
  };

  const liqs = liqSnap.docs
    .map((d) => stripInternal(d.data() as LiqDoc))
    .filter((l) => l?.date && Number(l?.totalUsd) > 0)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  // ── Precios: ÚLTIMO CIERRE, una lectura por ticker, todas en paralelo ────
  //
  // fase68 — LA FUENTE MANDA, NO EL TICKER. Hasta acá la excepción de forex
  // estaba escrita a mano para EUR (`filter(t => t !== 'EUR')`). CHF entró al
  // catálogo en fase54 y nadie extendió esa línea: se le pedía el cierre al
  // almacén de precios, que NUNCA lo tuvo, y caía en `priceMisses`. Con eso la
  // consola no podía valuar ninguna tenencia en francos —ni en la portada, ni
  // en Activos, ni en el capital por cliente de `clientes()`—.
  //
  // El composer sólo escribe en `price-history` los activos de fuente `cmc` y
  // `equity` (`price-history.backfill.ts` · `marketAssets()`); `forex` sale del
  // BCE vía Frankfurter y `peg` vale 1. Ahora el reparto se hace por
  // `priceSource`, que el catálogo ya proyecta al documento de cada moneda
  // (`currency-catalog.ts` · `projectCatalog`): un activo forex nuevo entra
  // solo, sin tocar este archivo.
  const year = new Date().getFullYear();
  const prices: Record<string, number> = {};
  const misses: string[] = [];
  const mercado = currencies.filter((c) => !c.pegUsd);
  const esForex = (c: CurRow) => String(c.priceSource ?? '') === 'forex';
  const enAlmacen = mercado.filter((c) => !esForex(c));
  const enForex = mercado.filter(esForex);

  await Promise.all([
    ...enAlmacen.map(async (c) => {
      const t = c.name;
      try {
        const doc = await db.collection('price-history').doc(`${t}_${year}`).get();
        let closes = (doc.data() as { closes?: Record<string, number> } | undefined)?.closes;
        // Sólo si el año en curso todavía no tiene cierres (los primeros días
        // de enero) se baja al anterior. Es la excepción, no el camino.
        if (!closes || !Object.keys(closes).length) {
          const prev = await db.collection('price-history').doc(`${t}_${year - 1}`).get();
          closes = (prev.data() as { closes?: Record<string, number> } | undefined)?.closes;
        }
        if (closes && Object.keys(closes).length) {
          const lastDay = Object.keys(closes).sort().pop() as string;
          prices[t] = Number(closes[lastDay]);
        }
      } catch {
        /* queda como miss */
      }
    }),
    // Divisas reales (EUR, CHF): {ticker}/USD del BCE vía Frankfurter, la
    // última cotización. Antes se pedía un RANGO de años —miles de días por
    // HTTP— para una serie que nadie dibujaba.
    //
    // fase68b — EL DOMINIO, COPIADO DEL QUE COBRA. `api.frankfurter.app` quedó
    // deprecado y hoy devuelve un 301 permanente a `api.frankfurter.dev/v1`. Un
    // 301 entre orígenes distintos NO lo sigue el navegador salvo que la
    // respuesta del redirect traiga sus propias cabeceras CORS, así que la
    // consola se quedó sin cotizar el euro el día de la migración y nadie se
    // enteró: la única señal era que EUR figuraba junto a CHF entre los
    // «faltantes» del almacén de precios, donde los dos parecían esperados.
    //
    // El composer (`helpers/common.ts`) y las dos superficies de cliente
    // (`services/price-feeds.ts`) ya estaban en el dominio nuevo; el BO era el
    // único que había quedado atrás. Acá se copia la llamada del composer al pie
    // de la letra —mismo host, mismo endpoint, mismos parámetros— porque es el
    // que le cobra al cliente. `scripts/check-frankfurter.mjs` lo fija.
    ...enForex.map(async (c) => {
      try {
        const r = await fetch(
          `https://api.frankfurter.dev/v1/latest?base=${encodeURIComponent(c.name)}&symbols=USD`
        );
        const j = (await r.json()) as { rates?: { USD?: number } };
        if (j?.rates?.USD) prices[c.name] = Number(j.rates.USD);
      } catch {
        /* miss */
      }
    }),
  ]);

  mercado.forEach((c) => {
    if (!(prices[c.name] > 0)) misses.push(c.name);
  });
  currencies.filter((c) => c.pegUsd).forEach((c) => (prices[c.name] = 1));

  return {
    loading: false,
    loadedAt: Date.now(),
    currencies, curById,
    tickerOf: (id: string) => curById[id]?.name ?? id,
    idOfTicker: (t: string) => idByTicker[String(t).toUpperCase()],
    users, userById, holds, prices, priceMisses: misses, liqs, gamification,
  };
}

async function leerSwaps(userById: Record<string, UserRow>): Promise<SwapTx[]> {
  const db = firebase.firestore();
  // Igualdad pura, sin índices (doctrina de la casa).
  const snap = await db.collection('transactions').where('action', '==', 'swap').get();
  const interno = (uid: string) =>
    !!userById[uid]?.interno || isInternalAccount(uid, userById[uid]?.email);
  const out: SwapTx[] = [];
  snap.docs.forEach((d) => {
    const x = d.data() as Record<string, any>;
    const dt = toD(x?.createdAt);
    const d0 = dt ? dt.toISOString().slice(0, 10) : null;
    if (String(x?.status ?? '') !== 'success' || !d0) return;
    const row: SwapTx = {
      uid: String(x?.uid ?? ''), d0,
      fromCur: String(x?.currency ?? ''), fromQty: Number(x?.amount ?? 0),
      toCur: String(x?.toCurrency ?? ''), toQty: Number(x?.toAmount ?? 0),
    };
    // El espejo valúa contra precios reales: un swap de vitrina fabricaría un
    // contrafactual ficticio. Fuera.
    if (row.uid && interno(row.uid)) return;
    if (row.uid && row.fromCur && row.toCur && row.fromQty > 0 && row.toQty > 0) out.push(row);
  });
  return out.sort((a, b) => (a.d0 < b.d0 ? -1 : 1));
}

// ── Contexto ─────────────────────────────────────────────────────────────────
const Ctx = createContext<Libro | null>(null);

export function LibroProvider({ children }: { children: React.ReactNode }) {
  const [snap, setSnap] = useState<Snap>(() => cache ?? VACIO);
  const [swaps, setSwaps] = useState<SwapTx[]>(() => swapsCache ?? []);
  const [swapsListos, setSwapsListos] = useState(() => swapsCache !== null);
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargar = useCallback(async (forzar = false) => {
    if (!forzar && cache && Date.now() - cache.loadedAt < VIGENCIA_MS) {
      setSnap(cache);
      return;
    }
    if (forzar) invalidarLibro();
    if (!enVuelo) {
      enVuelo = leerLibro()
        .then((s) => { cache = s; enVuelo = null; avisar(); return s; })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[consola] libro', err);
          enVuelo = null;
          const fallo = { ...(cache ?? VACIO), loading: false, loadedAt: Date.now() };
          cache = fallo;
          avisar();
          return fallo;
        });
    }
    const s = await enVuelo;
    if (vivo.current) setSnap(s);
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  // Cualquier instancia que refresque el caché despierta a las demás.
  useEffect(() => {
    const f = () => { if (vivo.current && cache) setSnap(cache); };
    oyentes.add(f);
    return () => { oyentes.delete(f); };
  }, []);

  const pedirProfundo = useCallback(() => {
    if (swapsCache) { setSwaps(swapsCache); setSwapsListos(true); return; }
    if (!swapsEnVuelo) {
      swapsEnVuelo = leerSwaps(cache?.userById ?? {})
        .then((s) => { swapsCache = s; swapsEnVuelo = null; return s; })
        .catch(() => { swapsEnVuelo = null; return []; });
    }
    void swapsEnVuelo.then((s) => {
      if (!vivo.current) return;
      setSwaps(s);
      setSwapsListos(true);
    });
  }, []);

  const value = useMemo<Libro>(
    () => ({ ...snap, swaps, swapsListos, pedirProfundo, reload: () => void cargar(true) }),
    [snap, swaps, swapsListos, pedirProfundo, cargar]
  );

  return React.createElement(Ctx.Provider, { value }, children);
}

export function useLibro(): Libro {
  const l = useContext(Ctx);
  if (!l) throw new Error('useLibro fuera de <LibroProvider>');
  return l;
}

/** Pide la segunda etapa (swaps) al montar. Sólo Analíticas · Tenencias. */
export function useLibroProfundo(): Libro {
  const l = useLibro();
  const { pedirProfundo } = l;
  useEffect(() => { pedirProfundo(); }, [pedirProfundo]);
  return l;
}

// ─────────────────────────────────────────────────────────────────────────────
// Derivaciones. Todas puras: entra el libro, salen números.
// ─────────────────────────────────────────────────────────────────────────────

export type AssetAgg = {
  id: string; ticker: string; qty: number; reserved: number; price: number | null;
  usd: number; reservedUsd: number; holders: number; costoAnual: number;
};

export function porActivo(l: Libro): AssetAgg[] {
  const m: Record<string, AssetAgg & { hold: Set<string> }> = {};
  l.holds.forEach((h) => {
    const c = l.curById[h.currency];
    const ticker = c?.name ?? h.currency;
    const price = c?.pegUsd ? 1 : l.prices[ticker] ?? null;
    const a = (m[h.currency] ??= {
      id: h.currency, ticker, qty: 0, reserved: 0, price,
      usd: 0, reservedUsd: 0, holders: 0, costoAnual: 0, hold: new Set<string>(),
    });
    a.qty += h.amount;
    a.reserved += h.reserved;
    a.hold.add(h.uid);
    if (price != null) {
      a.usd += (h.amount + h.reserved) * price;
      a.reservedUsd += h.reserved * price;
      a.costoAnual += ((h.amount + h.reserved) * price * aprDe(l, h.uid, h.currency)) / 100;
    }
  });
  return Object.values(m)
    .map((a) => ({ ...a, holders: a.hold.size }))
    .sort((a, b) => b.usd - a.usd);
}

/** APR efectivo que cobra el robot para un par cuenta/activo. Espejo del
 *  composer: Ultra paga su APR negociado por moneda (sin boost); el resto, el
 *  APR del catálogo más el boost de su categoría. Sin valor, Ultra paga 0. */
export function aprDe(l: Libro, uid: string, currencyId: string): number {
  const u = l.userById[uid];
  const c = l.curById[currencyId];
  if (!c || !c.enabled) return 0;
  if (u?.ultra) {
    const v = u.ultraApr?.[c.name];
    return Number.isFinite(Number(v)) ? Number(v) : 0;
  }
  return c.apr * (1 + (u?.boost ?? 0) / 100);
}

export function capitalDe(l: Libro, uid: string): number {
  return l.holds
    .filter((h) => h.uid === uid)
    .reduce((a, h) => {
      const c = l.curById[h.currency];
      const p = c?.pegUsd ? 1 : l.prices[c?.name ?? h.currency];
      return a + (p ? (h.amount + h.reserved) * p : 0);
    }, 0);
}

export function aum(l: Libro): number {
  return porActivo(l).reduce((a, t) => a + t.usd, 0);
}

export function clientes(l: Libro): Array<UserRow & { cap: number }> {
  // Un solo barrido de tenencias en vez de uno por cuenta: con 25 cuentas y
  // varios cientos de tenencias, `capitalDe` por usuario era cuadrático.
  const cap: Record<string, number> = {};
  l.holds.forEach((h) => {
    const c = l.curById[h.currency];
    const p = c?.pegUsd ? 1 : l.prices[c?.name ?? h.currency];
    if (p) cap[h.uid] = (cap[h.uid] ?? 0) + (h.amount + h.reserved) * p;
  });
  return l.users
    .filter((u) => !u.interno)
    .map((u) => ({ ...u, cap: cap[u.uid] ?? 0 }))
    .sort((a, b) => b.cap - a.cap);
}
